import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CaretDown } from '@phosphor-icons/react/dist/ssr/CaretDown';
import { SelectionRow } from '../UISystem/Forms/SelectionRow';
import { ButtonRounded } from '../UISystem/Buttons/ButtonRounded';
import { BORDER_RADIUS, SHADOWS } from '../../styles/UISystem';
import PillInput from '../UISystem/Forms/PillInput';
import {
  deleteCustomVenue,
  getCustomVenuesNear,
} from '../../services/location/providers/radarProvider';

/**
 * LocationOverlay - Venue selection bottom sheet
 *
 * Full bottom sheet overlay for selecting a venue.
 *
 * Two modes controlled by `showLocationContext`:
 * - true (default): "Where are you?" title, starts collapsed
 * - false: "Select venue" title, starts expanded (for editing)
 *
 * Both modes show the same UI: address pill with collapsible venue list.
 *
 * Behavior:
 * - Single selection: only one venue has checkmark at a time
 * - Tapping venue selects it (moves checkmark) but does NOT close
 * - Tapping "No venue" selects that option
 * - Tapping/focusing "Add venue" input deselects all list items
 * - Done button submits selection and closes
 * - Done is disabled if input is focused but empty
 *
 * Props:
 *   nearbyVenues - Array of venue objects from Radar ({ name, category, chain })
 *   currentVenue - Currently selected venue object (or null for "No venue")
 *   address - Address object with street, streetNumber, city, cityAbbr, neighborhood
 *   coords - Coordinates object with latitude/longitude (needed for deleting custom venues)
 *   showLocationContext - If true, shows "Where are you?" and starts collapsed.
 *                         If false, shows "Select venue" and starts expanded.
 *   onVenueChange - Callback when Done is pressed with selected venue
 *   onClose - Callback to dismiss overlay (backdrop tap or Done)
 */
const LocationOverlay = ({
  nearbyVenues = [],
  currentVenue,
  address = {},
  coords = {},
  showLocationContext = true,
  onVenueChange,
  onClose,
}) => {
  // Get valid custom venues from localStorage for this location
  // This ensures deleted custom venues don't appear in the list
  // Returns null if we can't check (no coords), meaning we should skip filtering
  const validCustomVenueNames = (() => {
    if (!coords.latitude || !coords.longitude) return null; // Can't validate without coords
    const customVenues = getCustomVenuesNear(coords.latitude, coords.longitude);
    return new Set(customVenues.map((v) => v.name.toLowerCase()));
  })();

  // Filter out any custom venues that no longer exist in localStorage
  // Then prepend currentVenue if it exists but isn't in the filtered list
  const effectiveNearbyVenues = (() => {
    // Filter nearbyVenues to remove deleted custom venues (only if we can validate)
    const filteredVenues = nearbyVenues.filter((v) => {
      // Keep non-custom venues
      if (!v.isCustom) return true;
      // If we can't validate (no coords), keep all custom venues
      if (validCustomVenueNames === null) return true;
      // Keep custom venues that still exist in localStorage
      return validCustomVenueNames.has(v.name.toLowerCase());
    });

    // If no currentVenue, return filtered list
    if (!currentVenue?.name) return filteredVenues;

    // If currentVenue is a deleted custom venue, don't add it
    // But only if we can validate - if we don't have coords, assume it's valid
    if (
      validCustomVenueNames !== null &&
      currentVenue.isCustom &&
      !validCustomVenueNames.has(currentVenue.name.toLowerCase())
    ) {
      return filteredVenues;
    }

    // If currentVenue already in list, return as-is
    const existsInList = filteredVenues.some((v) => v.name === currentVenue.name);
    if (existsInList) return filteredVenues;

    // Prepend the current venue (custom venue not in the original list)
    return [currentVenue, ...filteredVenues];
  })();

  // Expanded state - starts collapsed for "Where are you?", expanded for "Select venue"
  const [isExpanded, setIsExpanded] = useState(!showLocationContext);

  // Selection state: 'venue' (index), 'none', or 'custom' (for new input)
  const [selectionType, setSelectionType] = useState(() => {
    if (!currentVenue) return 'none';
    // Find index in effectiveNearbyVenues (which now includes custom venues)
    const index = effectiveNearbyVenues.findIndex((v) => v.name === currentVenue.name);
    return index >= 0 ? index : 'none';
  });
  // Only pre-fill input if venue isn't found in list (shouldn't happen normally)
  const [customVenueName, setCustomVenueName] = useState('');
  const [deletedVenues, setDeletedVenues] = useState([]);
  const inputRef = useRef(null);

  // Body scroll lock
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, []);

  // Handle venue row click
  const handleVenueSelect = (index) => {
    setSelectionType(index);
    setCustomVenueName('');
    // Blur input if it was focused
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  // Handle "No venue" click
  const handleNoVenueSelect = () => {
    setSelectionType('none');
    setCustomVenueName('');
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  // Handle deleting a custom venue
  const handleDeleteCustomVenue = (venueName, venueIndex) => {
    if (coords.latitude && coords.longitude) {
      deleteCustomVenue(venueName, coords.latitude, coords.longitude);
      // Remove from UI immediately
      setDeletedVenues((prev) => [...prev, venueName.toLowerCase()]);
      // If deleted venue was selected, change to "No venue"
      if (selectionType === venueIndex) {
        setSelectionType('none');
      }
      console.log('[LocationOverlay] Deleted custom venue:', venueName);
    }
  };

  // Handle input focus - deselects all list items
  const handleInputFocus = () => {
    setSelectionType('custom');
  };

  // Determine if Done should be disabled
  // Disabled when: input is the active selection AND it's empty
  const isDoneDisabled = selectionType === 'custom' && !customVenueName.trim();

  // Compute the venue to display in the address pill based on current selection
  const getDisplayVenue = () => {
    if (selectionType === 'custom' && customVenueName.trim()) {
      return { name: customVenueName.trim(), isCustom: true };
    } else if (selectionType === 'none') {
      return null;
    } else if (typeof selectionType === 'number') {
      return effectiveNearbyVenues[selectionType];
    }
    return null;
  };
  const displayVenue = getDisplayVenue();

  // Handle Done button
  const handleDone = () => {
    if (isDoneDisabled) return;

    let venueToSubmit = null;

    if (selectionType === 'custom' && customVenueName.trim()) {
      // Custom venue from input
      venueToSubmit = {
        name: customVenueName.trim(),
        category: null,
        chain: null,
        isCustom: true,
      };
    } else if (selectionType === 'none') {
      // No venue selected
      venueToSubmit = null;
    } else if (typeof selectionType === 'number') {
      // Venue from list
      venueToSubmit = effectiveNearbyVenues[selectionType];
    }

    // Call onVenueChange - parent handles save and close
    // Don't also call onClose, as that would double-save with different values
    if (onVenueChange) {
      onVenueChange(venueToSubmit);
    }
  };

  // Toggle expand/collapse
  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Render the venue list (used in both modes)
  const renderVenueList = () => (
    <>
      {/* Scrollable venue list */}
      <div
        style={{
          padding: '0 16px',
          maxHeight: '40vh',
          overflowY: 'auto',
        }}
      >
        {/* Neighborhood row FIRST (device pass 10 ruling): "no venue" and "the
            neighborhood" are the same display outcome, so the option is labeled
            with what it produces — a positive, concrete choice. Top placement
            because the picker only opens when the #1 guess was already wrong,
            and "just the street" is the strongest prior in that moment. */}
        <div style={{ marginBottom: '4px' }}>
          <SelectionRow
            label={address?.neighborhood || address?.cityAbbr || address?.city || 'No venue'}
            isSelected={selectionType === 'none'}
            onClick={handleNoVenueSelect}
          />
        </div>

        {/* Nearby venues from Radar */}
        {effectiveNearbyVenues.map((venue, index) => {
          // Skip deleted venues
          if (deletedVenues.includes(venue.name.toLowerCase())) {
            return null;
          }
          return (
            <div key={venue.name} style={{ marginBottom: '4px' }}>
              <SelectionRow
                label={venue.name}
                isSelected={selectionType === index}
                onClick={() => handleVenueSelect(index)}
                onDelete={
                  venue.isCustom ? () => handleDeleteCustomVenue(venue.name, index) : undefined
                }
              />
            </div>
          );
        })}
      </div>

      {/* Add venue input */}
      <div style={{ padding: '0 16px' }}>
        <div
          style={{
            marginTop: '8px',
            marginBottom: '16px',
          }}
        >
          <PillInput
            ref={inputRef}
            variant="secondary"
            value={customVenueName}
            onChange={(e) => {
              setCustomVenueName(e.target.value);
              // Also set selection to custom when typing (not just on focus)
              if (selectionType !== 'custom') {
                setSelectionType('custom');
              }
            }}
            onFocus={handleInputFocus}
            placeholder="Add venue"
          />
        </div>
      </div>
    </>
  );

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2100,
          background: 'rgba(0, 0, 0, 0.3)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
        }}
      >
        {/* Bottom sheet */}
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: '100%',
            backgroundColor: 'white',
            borderRadius: '24px 24px 0 0',
            boxShadow: SHADOWS.TIER3,
            display: 'flex',
            flexDirection: 'column',
            paddingBottom: 'calc(24px + env(safe-area-inset-bottom))',
          }}
        >
          {/* Header */}
          <div
            style={{
              textAlign: 'center',
              padding: '20px 20px 12px',
            }}
          >
            <span
              style={{
                fontSize: '18px',
                fontWeight: '500',
                color: '#333',
              }}
            >
              {showLocationContext ? 'Where are you?' : 'Select venue'}
            </span>
          </div>

          {/* Address pill with collapsible venue list - same for both modes */}
          {/* Current location display with caret */}
          <div
            style={{
              textAlign: 'center',
              padding: '0 20px 16px',
            }}
          >
            <div
              onClick={handleToggleExpand}
              style={{
                position: 'relative',
                backgroundColor: '#F9F9FB',
                borderRadius: BORDER_RADIUS.PILL,
                padding: '14px 40px 14px 16px', // Extra right padding for caret
                color: '#666',
                fontSize: '15px',
                cursor: 'pointer',
                textAlign: 'center',
              }}
            >
              {formatLocationDisplay(displayVenue, address)}
              <CaretDown
                size={18}
                color="#999"
                weight="bold"
                style={{
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: isExpanded
                    ? 'translateY(-50%) rotate(180deg)'
                    : 'translateY(-50%) rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              />
            </div>
          </div>

          {/* Expandable venue selection area */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                style={{ overflow: 'hidden' }}
              >
                {renderVenueList()}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Done button - always visible */}
          <div style={{ padding: '0 16px' }}>
            <ButtonRounded
              text="Done"
              onClick={handleDone}
              size="full"
              variant="primary"
              disabled={isDoneDisabled}
            />
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * Format the location display for the address pill
 * Returns JSX with venue name on its own line above the address
 */
function formatLocationDisplay(venue, address) {
  const addressLine = formatAddressShort(address);

  // If we have a venue, show venue name on first line, address on second
  if (venue?.name) {
    return (
      <>
        <div style={{ fontWeight: '500', color: '#333' }}>{venue.name}</div>
        {addressLine && <div style={{ marginTop: '2px' }}>{addressLine}</div>}
      </>
    );
  }

  // No venue - show just the address
  if (addressLine) return addressLine;

  // No location on the connection at all (denied permission / GPS failure) —
  // honest empty label; the input below is the recovery path
  return 'No place yet';
}

/**
 * Format a short address from address components
 */
function formatAddressShort(address) {
  if (!address) return '';

  const parts = [];

  // Street number + street
  if (address.streetNumber && address.street) {
    parts.push(`${address.streetNumber} ${address.street}`);
  } else if (address.street) {
    parts.push(address.street);
  }

  // City abbreviation
  if (address.cityAbbr) {
    parts.push(address.cityAbbr);
  } else if (address.city) {
    parts.push(address.city);
  }

  return parts.join(', ');
}

export default LocationOverlay;
